<template>
  <ul :class="theme_.grid">
    <li
      v-for="item in items"
      :key="item.value || item.label"
      class="inline-flex"
      @click="clickOnListItem(item)"
    >
      <vPopover
        v-if="item.options"
        @apply-show="$emit('open-dropdown', item, $event)"
        @apply-hide="$emit('close-dropdown', item, $event)"
      >
        <button
          class="whitespace-nowrap border-b-2 border-transparent"
          :class="[
            ...(item.theme || theme_.tab),
            { [theme_.active]: isSelected(item) },
          ]"
          v-bind="{ ...(itemComponent_.props || {}), ...(item.props || {}) }"
          v-on="{
            ...(itemComponent_.listeners || {}),
            ...(item.listeners || {}),
          }"
        >
          <AppSvgSymbol
            v-if="item.icon"
            :name="item.icon.name"
            :size="item.icon.size"
            :type="item.icon.type"
          />
          <span v-html="item.label" />
          <AppSvgSymbol
            :name="theme_.dropdownIcon.name"
            :size="theme_.dropdownIcon.size"
            :type="theme_.dropdownIcon.type"
          />
        </button>
        <template #popover>
          <div v-if="item.searchable" class="border-b flex flex-nowrap">
            <div class="py-4 pl-4">
              <AppSvgSymbol name="@escavador/search" size="18" />
            </div>
            <input
              class="p-4 border-none outline-none w-full text-sm"
              type="text"
              v-model="search[item.value || item.label]"
              placeholder="Pesquisar"
            />
          </div>
          <ul
            v-if="item.options && item.options.length"
            :class="theme_.dropdownList"
          >
            <li
              v-for="option in item.options.filter((option) =>
                optionSearch(option, search[item.value || item.label])
              )"
              :key="option.value || option.label"
              class="flex w-full"
              v-close-popover="closePopoverDirective(option, item)"
              @click="clickOnListItem(option, item)"
            >
              <component
                :is="getItemTag(option)"
                class="whitespace-nowrap flex w-full gap-2"
                :class="[
                  ...(option.theme || theme_.dropdownItem),
                  {
                    [theme_.dropdownActive]: isSelectedOption(option, item.key),
                  },
                ]"
                v-bind="getItemProps(option)"
                v-on="{
                  ...(itemComponent_.listeners || {}),
                  ...(option.listeners || {}),
                }"
              >
                <div
                  v-if="item.showCheckbox"
                  class="border w-4 h-4 border-gray-300 rounded-sm bg-white text-white"
                  :class="{
                    'bg-yellow-600': isSelectedOption(option, item.key),
                  }"
                >
                  <AppSvgSymbol name="@escavador/check" :size="14" />
                </div>
                <div
                  v-if="item.showCheckmark"
                  class="rounded-sm text-yellow-600"
                >
                  <AppSvgSymbol
                    :name="
                      isSelectedOption(option, item.key)
                        ? '@escavador/check'
                        : '@escavador/empty'
                    "
                    :size="16"
                  />
                </div>
                <span class="w-full" v-html="option.label" />
                <div v-if="item.showCheckmark">
                  <AppSvgSymbol name="@escavador/empty" :size="16" />
                </div>
              </component>
            </li>
          </ul>
          <div
            v-if="item.showControlButtons"
            class="p-4 flex gap-4 justify-between min-w-[250px]"
          >
            <button
              v-close-popover
              @click="$emit('cancel-changes', { key: item.key })"
              class="relative inline-flex flex-nowrap items-center justify-center gap-1 whitespace-nowrap rounded-lg py-3 px-4 text-center hover:bg-gray-100"
              :class="item?.controlButtons?.cancelButtonTheme || ''"
            >
              {{ item?.controlButtons?.submitButtonLabel || 'Cancelar' }}
            </button>
            <button
              v-close-popover
              @click="
                $emit('execute-changes', {
                  key: item.key,
                  value: selectedsList[item.key],
                })
              "
              class="relative inline-flex flex-nowrap items-center justify-center gap-1 whitespace-nowrap rounded-lg py-3 px-4 text-center bg-sky-600 text-white hover:bg-sky-700 hover:text-white"
              :class="item?.controlButtons?.submitButtonTheme || ''"
            >
              {{ item?.controlButtons?.submitButtonLabel || 'Filtrar' }}
            </button>
          </div>
        </template>
      </vPopover>
      <component
        v-else
        :is="getItemTag(item)"
        class="whitespace-nowrap border-b-2 border-transparent"
        :class="[
          ...(item.theme || theme_.tab),
          { [theme_.active]: isSelected(item) },
        ]"
        v-bind="getItemProps(item)"
        v-on="{
          ...(itemComponent_.listeners || {}),
          ...(item.listeners || {}),
        }"
      >
        <AppSvgSymbol
          v-if="item.icon"
          :name="item.icon.name"
          :size="item.icon.size"
          :type="item.icon.type"
        />
        <span v-html="item.label" />
      </component>
    </li>
  </ul>
</template>
<script>
import AppSvgSymbol from '../AppSvgSymbol'
import { latenize } from '@escavador/string-helper'
export const DEFAULT_THEME = {
  grid: [
    'grid',
    'grid-flow-col',
    'auto-cols-min',
    'gap-4',
    'border-b-[1px]',
    'border-brand-gray-200',
  ],
  tab: [
    'flex',
    'flex-nowrap',
    'gap-2',
    'items-center',
    'text-lg',
    'leading-6',
    'font-medium',
    'hover:border-brand-yellow-200',
    'text-gray-700',
    'hover:text-gray-800',
    'cursor-pointer',
  ],
  active: 'border-brand-yellow-400',
  dropdownList: [
    'w-full',
    'border-gray-300',
    'z-10',
    'rounded-lg',
    'bg-white',
    'overflow-hidden',
  ],
  dropdownItem: [
    'p-4',
    'text-xs',
    'leading-4',
    'font-normal',
    'text-gray-700',
    'hover:bg-gray-200',
    'cursor-pointer',
  ],
  dropdownIcon: {
    name: '@escavador/chevron-down',
    size: 24,
    type: 'baseline',
  },
}

export const DEFAULT_ITEM_COMPONENT = {
  tag: 'span',
  props: {},
}

export default {
  name: 'AppTabMenu',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: [String, Number],
      default: '',
    },
    selectedsList: {
      type: Object,
      default: () => ({}),
    },
    theme: {
      type: Object,
      default: () => DEFAULT_THEME,
    },
    tabIdentifier: {
      type: String,
      default: 'label',
    },
    itemComponent: {
      type: Object,
      default: () => DEFAULT_ITEM_COMPONENT,
    },
    currentSubdomain: {
      type: String,
      default: '',
    },
  },
  components: {
    AppSvgSymbol,
  },
  emits: [
    'open-dropdown',
    'close-dropdown',
    'change',
    'execute-changes',
    'cancel-changes',
  ],
  data() {
    return {
      search: {},
    }
  },
  computed: {
    theme_() {
      return {
        ...DEFAULT_THEME,
        ...this.theme,
      }
    },
    itemComponent_() {
      return {
        ...DEFAULT_ITEM_COMPONENT,
        ...this.itemComponent,
      }
    },
  },
  methods: {
    optionSearch(option, term) {
      return term
        ? latenize(option?.label || '').startsWith(latenize(term))
        : option
    },
    getItemTag(item) {
      const itemTag = item.tag || this.itemComponent_.tag

      const isRouterLink = itemTag === 'router-link'
      if (
        isRouterLink &&
        item.subdomain &&
        item.subdomain !== this.currentSubdomain
      ) {
        return 'a'
      }

      return itemTag
    },
    getItemProps(item) {
      const props = {
        ...(this.itemComponent_.props || {}),
        ...(item.props || {}),
      }

      if (this.getItemTag(item) === 'a') {
        props.href = item.href || item.props.href
        delete props.to
      }

      return props
    },
    closePopoverDirective(option, item) {
      if (item.showControlButtons) {
        return false
      }
      return option.closePopover !== undefined ? option.closePopover : true
    },
    clickOnListItem(item, parent = null) {
      if (!item.options) {
        const key = item?.key || parent?.key || null
        return this.$emit('change', {
          ...item,
          key,
          fromControllableList: parent?.showControlButtons || false,
        })
      }
    },
    isSelectedOption(item, key) {
      return (
        (this.selectedsList[key] &&
          this.selectedsList[key].map(String).includes(String(item.value))) ||
        item[this.tabIdentifier] === this.selected
      )
    },
    isSelected(item) {
      return (
        (item.key &&
          this.selectedsList[item.key] &&
          this.selectedsList[item.key].length) ||
        item[this.tabIdentifier] === this.selected
      )
    },
  },
}
</script>
